import React, { useState } from 'react';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('https://plencoplumbingltd.com/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit booking');
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'Visit Us',
      details: 'KIU World Saloon Building, Basement Floor Room B05',
      subdetails: 'Kansanga, Ggaba Rd, P.O Box 148303, Kampala, Uganda'
    },
    {
      icon: PhoneIcon,
      title: 'Call Us',
      details: '+256 200 906397',
      subdetails: 'Main Office Line'
    },
    {
      icon: PhoneIcon,
      title: 'Mobile',
      details: '+256 704 485986 | +256 786 272003',
      subdetails: 'Direct Lines - Available 24/7'
    },
    {
      icon: EnvelopeIcon,
      title: 'Email Us',
      details: 'info@plencoplumbingltd.com',
      subdetails: 'We reply within 24 hours'
    },
    {
      icon: ClockIcon,
      title: 'Working Hours',
      details: 'Monday - Saturday: 8:00 AM - 6:00 PM',
      subdetails: '24/7 Emergency Services Available'
    }
  ];

  return (
    <div className="bg-white">      <SEO 
        title="Contact Us - Get a Quote | Plenco Plumbing & Construction Kampala"
        description="Contact Plenco Plumbing & Construction for construction, plumbing, and tank calibration services in Uganda. Available 24/7. Call: +256 200 906397 | +256 704 485986 | +256 786 272003. Email: info@plencoplumbingltd.com. Located in Kampala, serving all of East Africa."
        keywords="contact plumbing company Uganda, get construction quote Kampala, emergency plumbing Kampala, 24/7 plumber Uganda, construction company contact Kampala, tank calibration contact"
        canonicalUrl="http://www.plencoplumbingltd.com/contact"
        type="website"
      />
            {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Get In</span>{' '}
            <span className="text-primary-200">Touch</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-primary-100 max-w-3xl mx-auto">
            Ready to start your project? Contact us today for a free consultation and detailed quotation
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Single Combined Card */}
          <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 md:gap-10">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-br from-primary to-primary-700 rounded-xl w-16 h-16 flex items-center justify-center mb-4 shadow-lg">
                    <info.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-black text-gray-900 mb-3">{info.title}</h3>
                  <p className="text-gray-700 font-bold mb-2 text-sm leading-relaxed">{info.details}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{info.subdetails}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16">
            {/* Form */}
            <div className="flex flex-col h-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-2 sm:mb-3">
                <span className="text-primary">Send Us</span> a Message
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
              <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="+256 XXX XXX XXX"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="How can we help?"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full h-full min-h-[150px] px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>
                
                {success && (
                  <div className="p-4 bg-green-50 border-2 border-green-500 rounded-xl text-green-700 font-semibold text-center">
                    Thank you! Your message has been received. We'll get back to you within 24 hours.
                  </div>
                )}
                
                {error && (
                  <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl text-red-700 font-semibold text-center">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary to-primary-700 text-white px-8 py-5 rounded-xl font-black text-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-xl hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Additional Info */}
            <div className="flex flex-col h-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-2 sm:mb-3">
                <span className="text-primary">Why Choose</span> Plenco?
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8">
                Here's what makes us the preferred choice for construction, plumbing, and engineering projects
              </p>
              <div className="space-y-5 flex-1 flex flex-col justify-between">
                <div className="flex items-start space-x-5 p-6 bg-gray-50 rounded-xl hover:bg-primary-50 transition-all duration-300">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center text-white font-black text-xl shadow-lg">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-black text-gray-900 mb-2 text-lg">Fast Response Time</h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]">We respond to all inquiries within 24 hours and offer emergency services available round the clock.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-5 p-6 bg-gray-50 rounded-xl hover:bg-primary-50 transition-all duration-300">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center text-white font-black text-xl shadow-lg">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-black text-gray-900 mb-2 text-lg">Professional Team</h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]">Our 36+ experienced professionals deliver quality work on every project with precision and expertise.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-5 p-6 bg-gray-50 rounded-xl hover:bg-primary-50 transition-all duration-300">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center text-white font-black text-xl shadow-lg">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-black text-gray-900 mb-2 text-lg">Regional Coverage</h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]">Operating across Uganda, Rwanda, South Sudan, and Tanzania with associate offices throughout East Africa.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-5 p-6 bg-gray-50 rounded-xl hover:bg-primary-50 transition-all duration-300">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center text-white font-black text-xl shadow-lg">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="font-black text-gray-900 mb-2 text-lg">15+ Years Experience</h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]">Proven track record since 2009 with over 200 completed projects across multiple sectors.</p>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="mt-auto bg-gradient-to-br from-primary to-primary-700 text-white p-8 rounded-2xl shadow-2xl">
                <h3 className="text-2xl font-black mb-3">24/7 Emergency Service</h3>
                <p className="mb-6 text-primary-100 text-lg">Need immediate assistance? Call us now!</p>
                <a
                  href="tel:+256786272003"
                  className="inline-flex items-center space-x-3 bg-white text-primary px-8 py-4 rounded-xl font-black hover:bg-primary-50 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <PhoneIcon className="h-6 w-6" />
                  <span className="text-lg">+256 786 272003</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Our</span> <span className="text-gray-600">Location</span>
            </h2>
            <p className="text-gray-600 text-lg mb-2">
              Visit us at KIU World Saloon Building, Basement Floor Room B05
            </p>
            <p className="text-gray-600">
              Kansanga, Ggaba Rd, P.O Box 148303, Kampala, Uganda
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=0.2944772,32.6046877&z=17&output=embed"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Contact Cards */}
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gradient-to-br from-primary to-primary-700 rounded-xl p-3 shadow-lg">
                    <PhoneIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 text-lg">Phone Numbers</h3>
                    <a href="tel:+256200906397" className="text-primary hover:text-primary-700 font-bold block">
                      +256 200 906397
                    </a>
                    <a href="tel:+256704485986" className="text-primary hover:text-primary-700 font-bold block">
                      +256 704 485986
                    </a>
                    <a href="tel:+256786272003" className="text-primary hover:text-primary-700 font-bold block">
                      +256 786 272003
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gradient-to-br from-primary to-primary-700 rounded-xl p-3 shadow-lg">
                    <EnvelopeIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 text-lg">Email</h3>
                    <a href="mailto:info@plencoplumbingltd.com" className="text-primary hover:text-primary-700 font-bold block">
                      info@plencoplumbingltd.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary to-primary-700 text-white p-6 rounded-xl shadow-2xl text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-black mb-2">24/7 Emergency</h3>
                <p className="mb-4 text-primary-100">Always Here When You Need Us</p>
                <a
                  href="tel:+256786272003"
                  className="inline-block bg-white text-primary px-6 py-3 rounded-xl font-black hover:bg-primary-50 transition-all shadow-lg hover:shadow-xl"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
